import React from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";

import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";

interface IBlogCardProps {
    className?: string;
    children: React.ReactNode;
    target?: "_self" | "_blank";
    href: string;
}

interface IBlogCardButtonProps {
    children: React.ReactNode;
    href?: string;
    onClick?: (e: React.MouseEvent) => void;
}

const BlogCardComponent = (props: IBlogCardProps) => {
    const target = useResponsiveLinkTarget();
    return (
        <a target={target} href={props.href} className={cx(blogCardBaseStyle, props.className)}>
            {props.children}
        </a>
    );
};

const BlogCardBody: React.FC<{children: React.ReactNode; className?: string}> = (props) => (
    <div className={cx(blogCardBodyStyle, props.className)}>{props.children}</div>
);

type IPictureProps = React.ComponentProps<typeof Picture>;
const CardPicture = (props: IPictureProps) => <Picture {...props} className={cardPictureStyle} />;

const HeaderText: React.FC<{children: React.ReactNode}> = (props) => (
    <Text as="p" variant="headline_2" fontFamily="secondary" className={headerTextStyle}>
        {props.children}
    </Text>
);

const SubheaderText: React.FC<{children: React.ReactNode; className?: string}> = (props) => (
    <Text as="p" variant="headline_6" className={cx(cardSubHeaderStyle, props.className)}>
        <span className={cardSubHeaderTextStyle}>{props.children}</span>
    </Text>
);

const CheckList = (props: {items: string[]}) => (
    <ul className={checkListStyle}>
        {props.items.map((item) => (
            <Text key={item} as="span" variant="body_copy_2" className={checkListItemStyle}>
                <CheckIcon size="1.7" className={checkIconStyle} />
                {item}
            </Text>
        ))}
    </ul>
);

const BulletList = (props: {items: string[]}) => (
    <ul className={bulletListStyle}>
        {props.items.map((item) => (
            <Text key={item} as="li" variant="body_copy_1">
                {item}
            </Text>
        ))}
    </ul>
);

const NextIcon = () => <ChevronRightIcon className={nextIconStyle} wrapperColor="#EBFF00" size="1.6" wrapperSize="3.2" />;

const BlogCardButton: React.FC<IBlogCardButtonProps> = (props) => (
    <Button variant="outlined_secondary" className={buttonStyle} href={props.href} onClick={props.onClick} target={props.href ? "_blank" : undefined}>
        {props.children}
    </Button>
);

export const BlogCard = Object.assign(BlogCardComponent, {
    Body: BlogCardBody,
    Picture: CardPicture,
    Header: HeaderText,
    SubHeader: SubheaderText,
    CheckList: CheckList,
    BulletList: BulletList,
    NextIcon: NextIcon,
    Button: BlogCardButton
});

const blogCardBaseStyle = css({
    cursor: "pointer",
    marginX: "auto",
    _hover: {
        color: "var(--colors-secondary)"
    },
    _focus: {
        color: "var(--colors-secondary)"
    },
    _active: {
        color: "var(--colors-secondary)"
    }
});

const blogCardBodyStyle = css({
    display: "block",
    borderRadius: 2,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    position: "relative",
    backgroundColor: "#fff",
    cursor: "pointer",
    marginX: "auto"
});

const cardPictureStyle = css({
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: "hidden"
});

const headerTextStyle = css({
    padding: 2
});

const cardSubHeaderStyle = css({
    padding: 2,
    marginBottom: 2
});

const cardSubHeaderTextStyle = css({
    display: "inline-block",
    md: {
        minHeight: "6rem"
    }
});

const checkListStyle = css({
    paddingTop: 0,
    paddingRight: 1,
    paddingBottom: 2,
    paddingLeft: 2
});

const checkListItemStyle = css({
    marginBottom: 2,
    display: "block"
});

const checkIconStyle = css({
    marginRight: 1
});

const bulletListStyle = css({
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 4,
    paddingLeft: 5,
    margin: 0
});

const nextIconStyle = css({
    marginLeft: "auto",
    position: "absolute",
    bottom: "1.6rem",
    right: "2.6rem"
});

const buttonStyle = css({
    marginTop: 0,
    marginRight: 0,
    marginBottom: 2,
    marginLeft: 2
});
